import { Component, EventEmitter, Input, Output } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { BaseComponent } from "src/app/base/base.component";
import {
  IdeaAttachmentFlow,
  IdeaAttachmentService,
} from "src/app/service/idea-attachment.service";
import { environment } from "src/environments/environment";
import {
  IDEA_ATTACHMENT_ACCEPT,
  IDEA_ATTACHMENT_MAX_BYTES,
  IDEA_ATTACHMENT_TYPES,
  MAX_IDEA_ATTACHMENTS,
} from "./idea-files.constants";

export interface IdeaFileView {
  id: string;
  file?: string;
  fileName?: string;
  originalName?: string;
  createdAt?: string;
}

/**
 * Anexos no detalhe da ideia (tradicional ou Kaizen): lista os ativos e, com
 * `canManage` (campo `canManageFiles` da API), permite adicionar e remover.
 * A lista vem do pai; depois de alterar, emite `changed` para o pai recarregar.
 */
@Component({
  selector: "app-idea-files",
  templateUrl: "./idea-files.component.html",
  styleUrls: ["./idea-files.component.scss"],
})
export class IdeaFilesComponent extends BaseComponent {
  @Input() flow: IdeaAttachmentFlow = "idea";
  @Input() ideaId: string;
  @Input() files: IdeaFileView[] = [];
  @Input() canManage = false;
  @Output() changed = new EventEmitter<void>();

  readonly accept = IDEA_ATTACHMENT_ACCEPT;
  readonly bucketUrl = environment.bucketUrl;

  pendingFiles: File[] = [];
  sending = false;

  constructor(
    public router: Router,
    public loading: NgxSpinnerService,
    public translate: TranslateService,
    private attachmentService: IdeaAttachmentService,
  ) {
    super(router, loading, translate);
  }

  get slots(): number {
    return Math.max(0, MAX_IDEA_ATTACHMENTS - (this.files?.length || 0));
  }

  fileUrl(f: IdeaFileView): string {
    return this.bucketUrl + (f.file || f.fileName);
  }

  onSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    const selected = Array.from(input.files || []);
    // permite escolher o mesmo arquivo de novo depois de tirá-lo da lista
    input.value = "";

    const accepted = [...this.pendingFiles];
    const rejected: string[] = [];

    selected.forEach((file) => {
      if (!this.isAllowedType(file)) {
        rejected.push(`${file.name}: ${this.translate.instant("attachments.errorType")}`);
      } else if (file.size > IDEA_ATTACHMENT_MAX_BYTES) {
        rejected.push(`${file.name}: ${this.translate.instant("attachments.errorSize")}`);
      } else if (accepted.length >= this.slots) {
        rejected.push(
          `${file.name}: ${this.translate.instant("attachments.errorLimit", { max: MAX_IDEA_ATTACHMENTS })}`,
        );
      } else {
        accepted.push(file);
      }
    });

    this.pendingFiles = accepted;

    if (rejected.length) {
      this.showMessage(this.translate.instant("baseComponent.warningAttention"), rejected.join("<br>"));
    }
  }

  onDiscard(index: number) {
    this.pendingFiles = this.pendingFiles.filter((_, i) => i !== index);
  }

  async onSend() {
    if (!this.pendingFiles.length || this.sending) {
      return;
    }
    this.sending = true;
    const results = await this.attachmentService.uploadAll(this.flow, this.ideaId, this.pendingFiles);
    this.sending = false;
    this.pendingFiles = [];
    this.changed.emit();

    const summary = this.attachmentService.summarize(results);
    if (summary) {
      this.showMessage(this.translate.instant("attachments.label"), summary.html, summary.type);
    }
  }

  onRemove(f: IdeaFileView) {
    this.confirmMessage(
      this.translate.instant("baseComponent.warningAttention"),
      this.translate.instant("attachments.confirmRemove"),
      () => this.remove(f),
    );
  }

  formatSize(bytes: number): string {
    const kb = bytes / 1024;
    return kb >= 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${Math.max(kb, 0.01).toFixed(2)} KB`;
  }

  private async remove(f: IdeaFileView) {
    const result =
      this.flow === "idea"
        ? await this.attachmentService.removeIdeaFile(f.id)
        : await this.attachmentService.removeKaizenFile(f.id);

    if (result.ok) {
      this.changed.emit();
      this.showMessage(
        this.translate.instant("attachments.label"),
        this.translate.instant("attachments.removed"),
        "success",
      );
    } else {
      this.showMessage(this.translate.instant("baseComponent.warningAttention"), result.message);
    }
  }

  private isAllowedType(file: File): boolean {
    const name = (file.name || "").toLowerCase();
    const dot = name.lastIndexOf(".");
    const mimetypes = dot >= 0 ? IDEA_ATTACHMENT_TYPES[name.slice(dot)] : undefined;
    return !!mimetypes && mimetypes.includes((file.type || "").toLowerCase());
  }
}
